import { useEffect } from 'react';
import { useForm, UseFormOptions } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { ITepEntryPass, ITepLease } from 'types/tep';
import { TepIntents } from 'constants/tep';
import { AllRoutes } from 'constants/routes';
import { IStoredTable } from 'types/redux/tep';
import { useTepColumns } from './useTepColumns';
import { localStorage } from 'core/app/storage';
import { useTableOffset } from 'hooks/useTableOffset';
import { useEntitiesTable } from 'hooks/useEntitiesTable';
import { leasesSelector } from 'core/redux/selectors/tep';
import { actions as leasesActions } from 'core/redux/api/veta/tep/leases';
import { actions as officersActions } from 'core/redux/api/veta/tep/officers';
import { actions as companiesActions } from 'core/redux/api/veta/tep/companies';

const FORM_OPTIONS: UseFormOptions<Partial<ITepLease>> = {
  mode: 'onChange',
};

export const useTepLeasesTable = () => {
  const dispatch = useDispatch();

  const {
    page,
    pageSize,
    sort,
    search,
    searchParams,
    columnsSorting,
    filters,
    updatePage,
    updatePageSize,
    sortColumnData,
    searchHandler,
  } = useEntitiesTable(AllRoutes.TepManagement);

  const { request, data, intent, marker, selected } = useSelector(leasesSelector);
  const { controlsRef, offset } = useTableOffset();

  const columns = useTepColumns();

  const formMethods = useForm<Partial<ITepLease>>(FORM_OPTIONS);

  const pageData = {
    no: page,
    size: pageSize.value,
    sort,
    search: search || undefined,
    filters: filters || '',
  };

  const fetchLeases = () => {
    dispatch(leasesActions.load.request({ payload: pageData }));
  };

  const fetchCompanies = () => {
    dispatch(companiesActions.load.request({ no: 1, size: 100 }));
  };

  const fetchOfficers = () => {
    dispatch(officersActions.load.request());
  };

  const exportTable = (format: string) => {
    const payload = { format, sort, search: search || undefined, filters: filters || '' };

    dispatch(leasesActions.export.request({ payload }));
  };

  const storeTable = (table: IStoredTable) => dispatch(leasesActions.setTableInstance(table));

  const onRowClick = (row: ITepEntryPass) => {
    dispatch(leasesActions.setMarker({ marker: marker?.id === row.id ? null : { id: row.id, uri: row.device.uri } }));
  };

  const controlProps = { ref: controlsRef, search, searchHandler, exportTable };

  const tableProps = {
    isLoading:
      request.isLoading && (!intent || intent === TepIntents.InlineEdit || intent === TepIntents.InlineUndraft),
    withMap: !!marker,
    offset,
    data: data?.objects || [],
    columns,
    selectedRowIds: selected.map(({ id }) => String(id)),
    clickedRowId: marker?.id ? String(marker.id) : undefined,
    hiddenColumnsIds: localStorage.get('tem-management-columns') || [],
    columnsSorting,
    sortColumnData,
    storeTable,
    onRowClick,
  };

  const footerProps = {
    totalCount: data?.total || 0,
    pageSize,
    page,
    onPageChange: updatePage,
    updatePageSize,
  };

  useEffect(() => {
    fetchCompanies();
    fetchOfficers();
  }, []);

  useEffect(() => {
    fetchLeases();
  }, [searchParams]);

  useEffect(() => {
    if (data?.objects?.length === 0 && page !== 1) {
      updatePage({ selected: page - 1 });
    }
  }, [data?.objects]);

  return { marker, formMethods, intent, controlProps, tableProps, footerProps };
};
